import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { FamilyMemberHistory } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemHeader, ItemTitle } from '../ui/item';
import { formatAge, formatBorn, formatDeceased, formatRelationship } from './familyMemberHistoryUtils';

export type FamilyMemberHistoryDetailProps = {
  familyMemberHistory: WithId<FamilyMemberHistory>;
  onBack?: () => void;
};

export const FamilyMemberHistoryDetail = ({
  familyMemberHistory: history,
  onBack,
}: FamilyMemberHistoryDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');

  const age = formatAge(history.ageAge ?? history.ageRange ?? history.ageString, t);
  const born = formatBorn(history);
  const deceased = formatDeceased(history, t);
  const recorded = history.date ? format(parseISO(history.date), DATE_FORMATS.DATE) : undefined;

  // Static `t()` calls so i18next-cli's static extractor can see each key.
  const statusLabel: Record<'partial' | 'health-unknown' | 'entered-in-error', string> = {
    partial: t('familyHistoryStatus.partial'),
    'health-unknown': t('familyHistoryStatus.health-unknown'),
    'entered-in-error': t('familyHistoryStatus.entered-in-error'),
  };

  const demographicItems = [
    { title: t('sex'), value: history.sex ? formatCodeableConcept(history.sex) : undefined },
    { title: t('age'), value: age },
    { title: t('born'), value: born },
    { title: t('vitalStatus'), value: deceased ?? t('alive') },
    { title: t('recordedDate'), value: recorded },
  ].filter((item) => item.value);

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      {onBack && (
        <Button
          id="back-to-family-history-button"
          variant="ghost"
          onClick={onBack}
          className="mb-4 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToFamilyHistory')}</span>
        </Button>
      )}

      <Item>
        <ItemHeader className="flex flex-col items-start gap-2">
          <div className="flex justify-center gap-2">
            {(history.status === 'partial' ||
              history.status === 'health-unknown' ||
              history.status === 'entered-in-error') && (
              <Badge variant="secondary">{statusLabel[history.status]}</Badge>
            )}
            {deceased && <Badge variant="outline">{deceased}</Badge>}
          </div>

          <ItemTitle className="text-lg font-medium">
            {formatRelationship(history.relationship, t)}
            {history.name ? <span className="ml-2 text-muted-foreground">· {history.name}</span> : null}
          </ItemTitle>
        </ItemHeader>
        <ItemContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {demographicItems.map((item) => (
              <div key={item.title}>
                <div className="mb-1 text-muted-foreground">{item.title}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>

          {history.condition && history.condition.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-medium text-muted-foreground">{t('conditions')}</h3>
              <div className="space-y-3">
                {history.condition.map((condition, index) => {
                  const onset =
                    formatAge(condition.onsetAge ?? condition.onsetRange ?? condition.onsetString, t) ??
                    (condition.onsetPeriod?.start
                      ? format(parseISO(condition.onsetPeriod.start), DATE_FORMATS.DATE)
                      : undefined);
                  const outcome = condition.outcome ? formatCodeableConcept(condition.outcome) : undefined;
                  const noteText = condition.note
                    ?.map((n) => n.text)
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div key={index} className="rounded-md border border-border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium">
                          {condition.code ? formatCodeableConcept(condition.code) : t('unknownCondition')}
                        </div>
                        {condition.contributedToDeath && (
                          <Badge variant="destructive" className="shrink-0">
                            {t('contributedToDeath')}
                          </Badge>
                        )}
                      </div>
                      {(onset || outcome) && (
                        <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                          {onset && (
                            <span>
                              {t('onset')}: {onset}
                            </span>
                          )}
                          {outcome && (
                            <span>
                              {t('outcome')}: {outcome}
                            </span>
                          )}
                        </div>
                      )}
                      {noteText && <p className="mt-2 text-sm text-muted-foreground">{noteText}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {history.note && history.note.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-medium text-muted-foreground">{t('notes')}</h3>
              <div className="space-y-2 text-sm">
                {history.note.map((note, index) => (
                  <p key={index}>{note.text}</p>
                ))}
              </div>
            </div>
          )}
        </ItemContent>
      </Item>
    </div>
  );
};
